import { firstNameOf } from '@/lib/personName'
import { PENDING_SELECTION_MESSAGE } from '@/lib/lockout'

// The Locked out screen (LockedOut board), shown by the `/locked-out` route and the App.vue
// overlay. Pure TypeScript so it can be unit-tested under Vitest's node environment.

export type LockoutReason = 'pending' | 'downgraded'

/**
 * Which story the screen tells. `authStore.subscriptionLockedOut` only holds a sentence: the
 * store's own PENDING_SELECTION_MESSAGE means the owner is still choosing; everything else (the
 * guard's `subscription_locked_out`, lib/api.ts's generic fallback) means a downgrade left this
 * person off the list.
 */
export function lockoutReason(message: string | null | undefined): LockoutReason {
  return message === PENDING_SELECTION_MESSAGE ? 'pending' : 'downgraded'
}

const KEPT = 'Nothing you worked on has been deleted.'

export interface LockedOutBodyInput {
  reason: LockoutReason
  orgName?: string | null | undefined
  freeSeats?: number | null | undefined
  /** The lockout sentence from the API / store, used only when neither the org nor the seats are known. */
  message?: string | null | undefined
}

/** The paragraph under "Your access is paused". */
export function lockedOutBody({ reason, orgName, freeSeats, message }: LockedOutBodyInput): string {
  const org = orgName?.trim() || null
  const whose = org ? `${org}’s` : 'Your workspace’s'

  if (reason === 'pending') {
    return `${whose} owner is choosing who stays active on the free plan. Everyone else is paused until they confirm the list. ${KEPT}`
  }

  const seats = typeof freeSeats === 'number' && freeSeats > 0 ? freeSeats : null
  if (!org && seats === null && message?.trim()) return `${message.trim()} ${KEPT}`

  const plan =
    seats === null
      ? `${whose} paid plan has ended.`
      : `${whose} paid plan has ended, and the free plan covers ${seats} ${seats === 1 ? 'person' : 'people'}.`
  return `${plan} The owner is choosing who stays active, and you aren’t on the list right now. ${KEPT}`
}

export interface LockoutOwner {
  name: string
  email: string
}

/**
 * "Email the owner": a mailto with a ready subject and a short note. Encoded with
 * encodeURIComponent so spaces are %20 and line breaks %0A (mail clients don't read "+" as a
 * space). Null without an owner email (the screen then says "Ask your organization owner").
 */
export function ownerMailto(owner: LockoutOwner | null | undefined, orgName: string | null | undefined): string | null {
  const to = owner?.email?.trim()
  if (!to) return null
  const first = firstNameOf(owner?.name)
  const org = orgName?.trim()
  const subject = 'Please restore my NerdCRM access'
  const body = [
    first ? `Hi ${first},` : 'Hi,',
    '',
    `${org ? `My access to ${org} on NerdCRM` : 'My NerdCRM access'} is paused. Could you add me back to the active list?`,
    '',
    'Thanks',
  ].join('\n')
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/** The dark pane: what happens to your work while you're paused. */
export const LOCKED_OUT_ASIDE = {
  title: 'While you’re paused, your work stays put.',
  bullets: [
    'Your deals, contacts and call recordings stay in the workspace.',
    'Your teammates can still see and work them.',
    'When the owner adds you back, you pick up exactly where you left off.',
  ],
} as const

/**
 * Where "Try again" leaves you once access is back. Your dashboard from:
 * - a signed-out page (a lockout right after signing in keeps you on /login, because the router
 *   refuses every app route while locked),
 * - the /locked-out page,
 * - nowhere: the first navigation was aborted (a hard load of a guarded route such as /pipeline
 *   hit the 403 in its beforeEnter), so the router is still at START_LOCATION and nothing is
 *   rendered to reload.
 * From an app page: stay, and reload its view in place.
 */
export function retryLanding(route: {
  name?: unknown
  meta: { requiresGuest?: unknown; [key: string]: unknown }
  matched: readonly unknown[]
}): 'dashboard' | 'stay' {
  if (route.matched.length === 0) return 'dashboard'
  return route.meta.requiresGuest || route.name === 'LockedOut' ? 'dashboard' : 'stay'
}

/** What the last "Try again" found: still refused, or the check itself failed (network). */
export type RetryNotice = 'still-locked' | 'failed'

/** The line under the note after a "Try again" that didn't get you back in. */
export function retryNoticeText(notice: RetryNotice | null | undefined): string {
  if (notice === 'still-locked') return 'Still paused. Your owner hasn’t added you back yet.'
  if (notice === 'failed') return 'Couldn’t check right now. Try again in a moment.'
  return ''
}
